import { connect } from 'react-redux';
import { useMemo, useCallback } from 'react';
import { Route, Routes, Link, useSearchParams } from 'react-router';
import { Tabs, Tab } from '@mui/material';
import BuildingsLoader from '../loaders/BuildingsLoader';
import OrganizationsLoader from '../loaders/OrganizationsLoader';
import BuildingsTable from './BuildingsTable';
import EquipmentTable from './EquipmentTable';
import PointsTable from './PointsTable';
import Trends from './Trends';
import TooltipTab from '../util/TooltipTab';
import EquipTypeLoader from '../loaders/EquipTypeLoader';
import PointTypeLoader from '../loaders/PointTypeLoader';
import LabelsLoader from '../loaders/LabelsLoader';
import TagsLoader from '../loaders/TagsLoader';
import MeasurementsLoader from '../loaders/MeasurementsLoader';
import { DeploymentsLoader } from '../loaders/deployments';
import UnitsLoader from '../loaders/UnitsLoader';
import { useRouteMatch } from '../../utils/url';
import {
  parameterUpdater,
  tryParse,
  extractSortingParams,
  extractFilterParams,
  sortingUpdater,
  filterUpdater,
  convertSelectionToList,
} from '../util/data-table';

function ExploreTabs({ searchParams, numSelectedBuildings, numSelectedPoints }) {
  // You need to provide the routes in descendant order.
  // This means that if you have nested routes like:
  // users, users/new, users/edit.
  // Then the order should be ['users/add', 'users/edit', 'users'].
  const routeMatch = useRouteMatch([
    '/explore/equipment',
    '/explore/points',
    '/explore/trends',
    '/',
  ]);
  const buildingsMessage = useMemo(() => {
    if (numSelectedBuildings === 0) return 'You must select at least 1 building before proceeding';
    if (numSelectedBuildings > 10)
      return 'You cannot select more than 10 buildings before proceeding';
    return null;
  }, [numSelectedBuildings]);
  const pointsMessage = useMemo(() => {
    if (numSelectedPoints === 0) return 'You must select at least 1 point before proceeding';
    if (numSelectedPoints > 100) return 'You cannot select more than 100 points before proceeding';
    return null;
  }, [numSelectedPoints]);

  const currentTab = routeMatch?.pattern?.path || '/';

  return (
    <Tabs textColor="secondary" indicatorColor="secondary" value={currentTab}>
      <Tab
        label="Buildings"
        value="/"
        to={`/explore/buildings?${searchParams.toString()}`}
        component={Link}
      />
      <TooltipTab
        value="/explore/equipment"
        message={buildingsMessage}
        label="Equipment"
        to={`/explore/equipment?${searchParams.toString()}`}
        component={Link}
      />
      <TooltipTab
        value="/explore/points"
        message={buildingsMessage}
        label="Points"
        to={`/explore/points?${searchParams.toString()}`}
        component={Link}
      />
      <TooltipTab
        value="/explore/trends"
        message={pointsMessage}
        label="Trends"
        to={`/explore/trends?${searchParams.toString()}`}
        component={Link}
      />
    </Tabs>
  );
}

const ExploreView = ({ buildingsById, orgsById, navigate }) => {
  // Snagged from https://github.com/TanStack/table/discussions/5002#discussioncomment-9778436
  const [searchParams, setSearchParams] = useSearchParams();
  const sorting = useMemo(() => extractSortingParams(searchParams), [searchParams]);
  const columnFilters = useMemo(() => extractFilterParams(searchParams), [searchParams]);

  // These are the primary source of truth for selections
  const buildingsSelection = useMemo(() => tryParse(searchParams.get('bids')), [searchParams]);
  const pointSelection = useMemo(() => tryParse(searchParams.get('pids')), [searchParams]);
  const equipmentSelection = useMemo(
    () =>
      Object.fromEntries(
        columnFilters.find(({ id }) => id === 'p_eids')?.value?.map((eid) => [eid, true]) ?? []
      ),
    [columnFilters]
  );

  const selectedBuildingsById = useMemo(
    () =>
      Object.fromEntries(
        convertSelectionToList(buildingsSelection)
          .map((bid) => buildingsById[bid])
          .filter(Boolean)
          .map((b) => [b.id.toString(), b])
      ),
    [buildingsSelection, buildingsById]
  );

  const sortingUpdate = useMemo(() => sortingUpdater(sorting), [sorting]);
  const filtersUpdate = useMemo(() => filterUpdater(columnFilters), [columnFilters]);

  const buildingSelectionUpdate = useCallback(
    (updater) => parameterUpdater('bids', updater, buildingsSelection),
    [buildingsSelection]
  );

  const equipmentSelectionUpdate = useCallback(
    (updater) =>
      filtersUpdate((cfs) => {
        const newEquipmentSelection = convertSelectionToList(
          typeof updater === 'function' ? updater(equipmentSelection) : updater
        );
        const peid = { id: 'p_eids', value: newEquipmentSelection };
        return [...cfs.filter(({ id }) => id !== 'p_eids'), peid];
      }),
    [filtersUpdate, equipmentSelection]
  );

  const pointSelectionUpdate = useCallback(
    (updater) => parameterUpdater('pids', updater, pointSelection),
    [pointSelection]
  );

  if (Object.keys(buildingsById).length <= 0)
    return (
      <>
        <OrganizationsLoader />
        <BuildingsLoader />
        <EquipTypeLoader />
        <PointTypeLoader />
        <LabelsLoader />
        <TagsLoader />
        <MeasurementsLoader />
      </>
    );
  return (
    <div>
      <OrganizationsLoader />
      <BuildingsLoader />
      <EquipTypeLoader />
      <PointTypeLoader />
      <LabelsLoader />
      <TagsLoader />
      <MeasurementsLoader />
      <UnitsLoader />
      <DeploymentsLoader />
      <ExploreTabs
        searchParams={searchParams}
        numSelectedBuildings={Object.keys(selectedBuildingsById || {}).length}
        numSelectedPoints={Object.keys(pointSelection || {}).length}
      />
      <Routes>
        <Route
          path="/explore/equipment"
          element={
            <EquipmentTable
              navigate={navigate}
              sorting={sorting}
              onSortingChange={(updater) => setSearchParams(sortingUpdate(updater))}
              columnFilters={columnFilters}
              onColumnFiltersChange={(updater) => setSearchParams(filtersUpdate(updater))}
              buildingsById={selectedBuildingsById}
              orgsById={orgsById}
              equipmentSelection={equipmentSelection}
              onRowSelectionChange={(updater) => setSearchParams(equipmentSelectionUpdate(updater))}
            />
          }
        />
        <Route
          path="/explore/points"
          element={
            <PointsTable
              sorting={sorting}
              onSortingChange={(updater) => setSearchParams(sortingUpdate(updater))}
              columnFilters={columnFilters}
              onColumnFiltersChange={(updater) => setSearchParams(filtersUpdate(updater))}
              buildingsById={selectedBuildingsById}
              orgsById={orgsById}
              pointSelection={pointSelection}
              onRowSelectionChange={(updater) => setSearchParams(pointSelectionUpdate(updater))}
            />
          }
        />
        <Route
          path="/explore/trends"
          element={
            <Trends
              buildingsById={selectedBuildingsById}
              pointIds={Object.entries(pointSelection)
                // eslint-disable-next-line no-unused-vars
                .filter(([_pid, include]) => include)
                .map(([pid]) => pid)}
            />
          }
        />
        <Route
          path="*"
          element={
            <BuildingsTable
              navigate={navigate}
              sorting={sorting}
              onSortingChange={(updater) => setSearchParams(sortingUpdate(updater))}
              columnFilters={columnFilters}
              onColumnFiltersChange={(updater) => setSearchParams(filtersUpdate(updater))}
              buildingsById={buildingsById}
              orgsById={orgsById}
              buildingsSelection={buildingsSelection}
              onRowSelectionChange={(updater) => setSearchParams(buildingSelectionUpdate(updater))}
            />
          }
        />
      </Routes>
    </div>
  );
};

const mapStateToProps = (state) => {
  const {
    entities: {
      buildings: { byId: buildingsById },
    },
    users: { orgsById },
  } = state;
  return {
    buildingsById,
    orgsById,
  };
};

// eslint-disable-next-line no-unused-vars
const mapDispatchToProps = (dispatch) => ({});

export default connect(mapStateToProps, mapDispatchToProps)(ExploreView);
